import { Fab } from "@rmwc/fab";
import { Icon } from "@rmwc/icon";
import { TextField } from "@rmwc/textfield";
import { selectUser } from "App/Authentification/Store/selectors";
import { ACCOUNT_MODE, THEME, URL_BASE } from "App/Constants";
import { showError, showSuccess } from "App/Toast/Toast";
import { RULES_PASSWORD } from "Config.json";
import _ from "lodash";
import { RESOURCE_ACCOUNT, RESOURCE_ACCOUNT_INFO, RESSOURCE_PASSWORD } from "Login/API/LoginAPI";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GET, PATCH, POST } from "Request/Components/RequestUtils";
import { GET_SINGLE, useJsonRequest } from "Request/Components/useJsonRequest";
import { DATA_CREATED, DATA_UPDATED } from "Request/Store/constants";
import { selectDataResourceReceived, selectStateDataResource } from "Request/Store/selectors";
import zxcvbn from "zxcvbn";
import "./Account.css";

// #region FONCTION COMPOSANT
/**
* Composant permettant de créer un compte utilisateur
* @class Account
* @category Account
*/
function Account(props) {
  // #region INITIALISATION
  const {
    mode
  } = props;

  // Initialisation du state
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstname, setFirstname] = useState("");
  const [immatriculation, setImmatriculation] = useState("");
  const [login, setLogin] = useState("");
  const [lastname, setLastname] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState(RULES_PASSWORD);

  // Initialisation des sélecteurs
  const dataReceived = useSelector((state) => selectDataResourceReceived(state, RESOURCE_ACCOUNT_INFO)); // Données issues de la requête
  const stateUser = useSelector((state) => selectStateDataResource(state, RESOURCE_ACCOUNT)); // Données issues de la requête
  const dataUpdated = useSelector((state) => selectStateDataResource(state, RESSOURCE_PASSWORD)); // Données issues de la requête
  const userInfos = useSelector((state) => selectUser(state));

  // Traduction i18n
  const { t } = useTranslation();

  // Navigation
  const history = useHistory();
  // #endregion

  // #region UTILS
  //AYB 2026.02.26 - Normalise l'email en supprimant tous les espaces (accepte la saisie avec espaces, envoi sans espaces)
  const normalizeEmail = (email) => (email && typeof email === 'string' ? email.replace(/\s/g, '') : '');

  const getLabel = () => {
    let label = "";

    switch (passwordStatus.strength) {
      case 0:
        label = t("password.bad")
        break;
      case 1:
        label = t("password.weak")
        break;
      case 2:
        label = t("password.good")
        break;
      case 3:
        label = t("password.strong")
        break;
      case 4:
        label = t("password.veryStrong")
        break;

      default:
        break;
    }

    return label;
  }

  // Validation du formulaire
  const isValidData = () => {
    if (mode === ACCOUNT_MODE.creation) {
      const validateEmail = (email) => {
        if (!email || typeof email !== 'string') {
          return false;
        }
        const normalized = normalizeEmail(email);
        if (!normalized) {
          return false;
        }
        const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(normalized)) {
          return false;
        }
        if (normalized.length > 254) {
          return false;
        }
        if (/\.{2,}/.test(normalized) || normalized.startsWith('.') || normalized.startsWith('@') || normalized.endsWith('.')) {
          return false;
        }
        return true;
      };

      if (!login || !password || !confirmPassword || !confirmEmail) {
        showError(t("login.mailPasswordRequired"), { useOverlay: true, title: t("login.connection") });
        return false;
      }

      if (!validateEmail(login)) {
        showError(t("login.mailIncorrect"), { useOverlay: true, title: t("login.connection") });
        return false;
      }

      if (normalizeEmail(login) !== normalizeEmail(confirmEmail)) {
        showError(t("login.emailNotSame"), { useOverlay: true, title: t("login.connection") });
        return false;
      }

      if (!immatriculation || immatriculation.trim() === "") {
        showError(t("achat.needImmatriculation"), { useOverlay: true, title: t("login.connection") });
        return false;
      }
    }

    // Mot de passe différent de la confirmation
    if (password !== confirmPassword) {
      showError(t("login.passwordNotSame"), { useOverlay: true, title: t("login.connection") });
      return false;
    }

    if (parseInt(passwordStatus.strength) < 3) {
      showError(t("login.passwordForceTooLow"), { useOverlay: true, title: t("login.connection") });
      return false;
    }

    return true;
  };

  // Validation du mot de passe
  const isValidPassword = (pwd) => {
    let rules = RULES_PASSWORD;

    // Vérification de la présence d'au moins un chiffre
    if (rules.needDigit) {
      rules.haveDigit = true;
      const haveDigitRegex = /[0-9]/gm;
      if (!password.match(haveDigitRegex)) {
        rules.haveDigit = false;
      }
    }

    // Vérification de la présence d'au moins un caractère spécial obligatoire
    if (rules.specialCharsList !== "") {
      rules.haveSpecialChars = true;
      // Création de la regex
      const specialCharRegex = "^.$|?*+()[]{}/\\";
      let regexWithSpecialChars = "";
      for (let i = 0; i < rules.specialCharsList.length; i++) {
        // Echappement des caractères spéciaux
        let char = rules.specialCharsList.charAt(i);
        if (specialCharRegex.indexOf(char) !== -1) {
          char = "\\" + char;
        }

        regexWithSpecialChars = regexWithSpecialChars + char;
        if (i !== rules.specialCharsList.length - 1) {
          regexWithSpecialChars = regexWithSpecialChars + "|";
        }
      }

      // Test de la présence d'un caractère spécial
      const haveSpecialRegex = new RegExp(regexWithSpecialChars, "gi");
      if (!password.match(haveSpecialRegex)) {
        rules.haveSpecialChars = false;
      }
    }

    // Vérification de la longueur du mot de passe
    if (rules.minlength) {
      rules.haveMinlength = password.length < rules.minlength ? false : true;
    }

    // Vérification de la présence d'au moins une minuscule et une majuscule
    if (rules.needMixcase) {
      rules.haveMixcase = true;
      const haveMixCaseRegex = /(?=.*[a-z])(?=.*[A-Z])/gm;
      if (!password.match(haveMixCaseRegex)) {
        rules.haveMixcase = false;
      }
    }

    // Mise à jour de la force du mot de passe
    rules.strength = zxcvbn(pwd).score;

    setPasswordStatus({ ...rules });
  };
  // #endregion

  // #region REQUEST
  const requestGetUserInfos = useJsonRequest({
    command: GET,
    getMode: GET_SINGLE,
    resource: RESOURCE_ACCOUNT_INFO,
    url: URL_BASE,
  });

  // Requête de création de compte
  const requestPostNewAccount = useJsonRequest({
    command: POST,
    resource: RESOURCE_ACCOUNT,
    tokenObligatory: false,
    tokenAdminObligatory: true,
    url: URL_BASE,
  });

  // Requête de modification de compte
  const requestPatchNewAccount = useJsonRequest({
    command: PATCH,
    resource: RESOURCE_ACCOUNT_INFO,
    url: URL_BASE,
  });
  // #endregion

  // #region EVENTS
  const handleSubmitAccount = () => {
    let dataToSend = {};

    // Gestion du PATCH
    const submitPatch = () => {
      if (!immatriculation || immatriculation.trim() === "") {
        showError(t("achat.needImmatriculation"), { useOverlay: true, title: t("login.connection") });
        return;
      }

      dataToSend = {
        email: normalizeEmail(login),
        firstName: firstname,
        name: lastname,
        plate: immatriculation,
        username: normalizeEmail(login),
      };

      if (password) {
        dataToSend.password = password;
      }

      requestPatchNewAccount(null, dataToSend);
    };

    // Gestion du POST
    const submitPost = () => {
      if (!isValidData()) {
        return;
      }

      dataToSend = {
        email: normalizeEmail(login),
        firstName: firstname,
        name: lastname,
        password: password,
        plate: immatriculation,
        typeControl: 0,
        username: normalizeEmail(login),
      }

      requestPostNewAccount(dataToSend)
    }

    // Requête associée
    mode === ACCOUNT_MODE.creation ? submitPost() : submitPatch();
  };
  // #endregion

  // #region HOOK D'EFFET
  // Vérification du mot de passe
  useEffect(() => {
    isValidPassword(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password])

  // Requête des données du compte
  useEffect(() => {
    if (mode !== ACCOUNT_MODE.modification) {
      return;
    }

    requestGetUserInfos();
  }, [mode, requestGetUserInfos, userInfos]);

  // Récupération des données du compte
  useEffect(() => {
    if (!dataReceived) {
      return;
    }

    // Au retour du PATCH, on reçoit un objet et non un tableau
    let d = {};

    if (dataReceived && Array.isArray(dataReceived)) {
      d = _.cloneDeep(dataReceived[0]);

      setFirstname(d.firstName || "");
      setImmatriculation(d.plate);
      setLastname(d.name);
      setLogin(d.email);
    } else {
      showSuccess(t("login.accountUpdated"));
      history.goBack();
      return;
    }

  }, [dataReceived, history, t]);

  // Réception de la créaation
  useEffect(() => {
    if (stateUser !== DATA_CREATED) {
      return;
    }

    showSuccess(t("login.accountCreated"));

    history.goBack();
  }, [stateUser, mode, t, history]);

  // Réception de la mise à jour des informations de compte
  useEffect(() => {
    if (stateUser !== DATA_UPDATED) {
      return;
    }

    showSuccess(t("login.accountUpdated"));

    history.goBack();
  }, [stateUser, mode, t, history]);

  // Réception de la mise à jour des informations de mot de passe
  useEffect(() => {
    if (dataUpdated !== DATA_UPDATED) {
      return;
    }

    showSuccess(t("login.accountUpdated"));

    history.goBack();
  }, [dataUpdated, mode, t, history]);
  // #endregion

  // #region INTERFACE
  const forcePasswordInterface = () => (
    <>
      <div className="Account-ForcePassword-Label">
        {t("password.force")}<div className="Account-ForcePassword-Strength" data-strength={passwordStatus.strength}>&nbsp;{getLabel()}</div>
      </div>
      <div className="Account-InfoSecurityContainer">
        <Icon icon={{ icon: "lock" }} />
        {t("password.passwordContains")}
      </div>
      <div className="Account-RulesContainer">
        {passwordStatus.minlength > 1
          ?
          <div className="Account-RulesRow">
            <Icon
              icon={{ icon: password && passwordStatus.haveMinlength ? "check_circle_outline" : "cancel" }}
              style={{ color: password && passwordStatus.haveMinlength ? THEME.ON_VALID : THEME.ON_ERROR }}
            />
            <div>&nbsp; {t("password.atLeast")} {passwordStatus.minlength} {t("password.atLeastChar")}</div>
          </div>
          :
          null
        }
        {passwordStatus.needMixcase
          ?
          <div className="Account-RulesRow">
            <Icon
              icon={{ icon: password && passwordStatus.haveMixcase ? "check_circle_outline" : "cancel" }}
              style={{ color: password && passwordStatus.haveMixcase ? THEME.ON_VALID : THEME.ON_ERROR }}
            />
            <div>&nbsp; {t("password.atLeastUppercaseAndLowercase")}</div>
          </div>
          :
          null
        }
        {passwordStatus.needDigit
          ?
          <div className="Account-RulesRow">
            <div className="Account-RulesResult">
              <Icon
                icon={{ icon: password && passwordStatus.haveDigit ? "check_circle_outline" : "cancel" }}
                style={{ color: password && passwordStatus.haveDigit ? THEME.ON_VALID : THEME.ON_ERROR }}
              />
            </div>
            <p>&nbsp; {t("password.atLeastNumber")}</p>
          </div>
          :
          null
        }
        {passwordStatus.needSpecialChars && passwordStatus.specialCharsList !== ""
          ?
          <div className="Account-RulesRow">
            <Icon
              icon={{ icon: password && passwordStatus.haveSpecialChars ? "check_circle_outline" : "cancel" }}
              style={{ color: password && passwordStatus.haveSpecialChars ? THEME.ON_VALID : THEME.ON_ERROR }}
            />
            <div>
              &nbsp; {t("password.atLeastSpecialChar")}
              <br />
              <span style={{ fontWeight: "bold" }}>{passwordStatus.specialCharsList.replace(" ", "(espace)")}</span>
            </div>
          </div>
          :
          null
        }
      </div>
    </>
  );


  return (
    <div className="Account-Container">
      <div className="Account-Input-Container">

        {mode === ACCOUNT_MODE.creation ?
          <>
            <div className="Account-Title">
              {t("login.accountTitleCreate")}
            </div>
            <TextField
              className="Account-Input"
              label={t("login.mail")}
              name="login"
              type="email"
              onChange={(item) => setLogin(item.target.value)}
              outlined
              required
              value={login}
            />
            <TextField
              className="Account-Input"
              label={t("login.confirmMail")}
              name="confirmEmail"
              type="email"
              onChange={(item) => setConfirmEmail(item.target.value)}
              outlined
              required
              invalid={normalizeEmail(login) !== "" && normalizeEmail(confirmEmail) !== "" && normalizeEmail(login) !== normalizeEmail(confirmEmail)}
              value={confirmEmail}
            />
          </>
          :
          <>
            <div className="Account-Title">
              {t("login.accountTitleModify")}
            </div>
            <TextField
              className="Account-Input Account-Input--readonly"
              label={t("login.mail")}
              name="email"
              type="email"
              outlined
              value={userInfos?.email ?? login}
              disabled
            />
          </>
        }
        <TextField
          className="Account-Input"
          label={t("login.firstname")}
          name="firstname"
          onChange={(item) => setFirstname(item.target.value)}
          outlined
          value={firstname}
        />
        <TextField
          className="Account-Input"
          label={t("login.lastname")}
          name="lastname"
          onChange={(item) => setLastname(item.target.value)}
          outlined
          value={lastname}
        />
        <TextField
          className="Account-Input"
          label={t("login.immatriculation")}
          name="immatriculation"
          onChange={(item) => setImmatriculation(item.target.value)}
          outlined
          required
          value={immatriculation}
        />
        <TextField
          className="Account-Input"
          floatLabel
          label={t("login.password")}
          name="new-password"
          autoComplete="new-password"
          onChange={(item) => setPassword(item.target.value)}
          outlined
          required
          type={showPassword ? "text" : "password"}
          trailingIcon={{
            icon: showPassword ? "visibility_off" : "visibility",
            size: "small",
            tabIndex: 0,
            onClick: () => setShowPassword(!showPassword)
          }}
          value={password}
        />
        <div className="Account-ForcePassword-Container">
          <TextField
            className="Account-Input"
            floatLabel
            label={t("login.confirmPassword")}
            name="new-password-confirm"
            autoComplete="new-password"
            onChange={(item) => setConfirmPassword(item.target.value)}
            outlined
            required
            type={showConfirmPassword ? "text" : "password"}
            trailingIcon={{
              icon: showConfirmPassword ? "visibility_off" : "visibility",
              size: "small",
              tabIndex: 0,
              onClick: () => setShowConfirmPassword(!showConfirmPassword)
            }}
            invalid={password !== confirmPassword}
            value={confirmPassword}
          />
          <div className="Account-ConfirmPassword-Label"> {t("password.newpasswordAndConfirm")} </div>
          {/* Force du mot de passe */}
          {forcePasswordInterface()}
        </div>
        <div className="Account-Button-Container">
          <Fab
            label={t("login.validation")}
            onClick={() => handleSubmitAccount()}
          />
          {mode === ACCOUNT_MODE.creation
            ?
            <p className="Login-Link" onClick={() => history.goBack()} style={{ textDecoration: "none" }}>
              {t("login.backToLogin")}
            </p>
            :
            null
          }
        </div>
      </div>
    </div>
  );
  // #endregion
};
// #endregion

export { Account };

